import { memo, useState, useCallback, useEffect } from 'react';
import { SettingOutlined } from '@ant-design/icons';
import { Button, Dropdown } from 'antd';
import { useToggle } from '@/hooks';
import { cn } from '@/libs/utils';

export default function TableActionDropdown({
  children,
  options,
  disabled,
  label,
}) {
  // * TOGGLE: action dropdown
  const { open, onToggle, onClose } = useToggle(false);

  const [selectedValue, setSelectedValue] = useState(null);

  useEffect(() => {
    !open && setSelectedValue(null);
    return () => setSelectedValue(null);
  }, [open]);

  const handleClose = useCallback(() => {
    setSelectedValue(null);
  }, []);

  const handleChange = useCallback((select) => {
    setSelectedValue(select);
  }, []);

  return (
    <Dropdown
      open={open}
      onOpenChange={onToggle}
      placement="bottomRight"
      trigger={['click']}
      popupRender={() => (
        <RenderDropdownContent
          open={open}
          label={label}
          options={options}
          onChange={handleChange}
          onClose={handleClose}
          onRemove={onClose}
          selected={selectedValue}
        >
          {children}
        </RenderDropdownContent>
      )}
      overlayStyle={{ zIndex: 1000 }}
    >
      <Button
        type="default"
        className={cn(
          'btn-default btn-default-active h-8 bg-secondary text-black hover:!bg-secondary'
        )}
        icon={<SettingOutlined />}
        disabled={disabled}
      >
        <span>{label}</span>
      </Button>
    </Dropdown>
  );
}

const RenderDropdownContent = memo(
  ({
    children,
    open,
    label,
    options,
    onChange,
    onClose,
    onRemove,
    selected,
  }) => {
    const dotButtonAnimation = (dotColor) => {
      return (
        <span
          className={cn(
            'block h-2 w-2 shrink-0 rounded-full',
            dotColor ? dotColor : 'bg-zinc-100'
          )}
        >
          <span
            className={cn(
              'block h-2 w-2 shrink-0 rounded-full group-hover:animate-ping',
              dotColor ? dotColor : 'bg-zinc-100'
            )}
          />
        </span>
      );
    };

    if (typeof children !== 'function') {
      throw Error(
        'Invalid children prop: Expected a function as children. Please pass a function that returns React elements, e.g., {({ open, onClose }) => <YourComponent open={open} onClose={onClose} />}.'
      );
    }

    return (
      <>
        <div className="grid max-h-[340px] min-w-[180px] gap-3 overflow-y-auto rounded-md border-[3px] border-accent bg-white p-3 shadow-md">
          <header>
            <span className="font-semibold">{label}:</span>
          </header>

          <div className="grid gap-2">
            {Object.entries(options).map(([label, values]) => {
              return (
                <div key={label.toLocaleLowerCase()} className="grid gap-2">
                  <span>{label}</span>

                  <ul className="grid gap-2">
                    {values.map(({ key, label, dotColor }) => {
                      if (key.includes('remove')) {
                        return (
                          <li key={key}>
                            <Button
                              type="text"
                              onClick={() => onChange(key)}
                              className={cn(
                                'btn-default group h-7 w-full shrink-0 justify-start pl-2 pr-3 text-red-500 hover:!text-red-500'
                              )}
                            >
                              {dotButtonAnimation(dotColor)}

                              <span>{label}</span>
                            </Button>
                          </li>
                        );
                      }

                      return (
                        <li key={key}>
                          <Button
                            type="text"
                            onClick={() => onChange(key)}
                            className={cn(
                              'btn-default group h-7 w-full shrink-0 justify-start pl-2 pr-3 hover:!text-zinc-700'
                            )}
                          >
                            {dotButtonAnimation(dotColor)}

                            <span>{label}</span>
                          </Button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>

        {children({
          open,
          onClose,
          onRemove,
          selected,
        })}
      </>
    );
  }
);

RenderDropdownContent.displayName = 'RenderDropdownContent';
